import { forwardRef, useState, type InputHTMLAttributes } from 'react'

import './passwordInput.css'

/**
 * Password input with a show/hide eye toggle. Shared between the owner/staff
 * (`.owner-*`) and admin (`.admin-*`) theme trees — `classPrefix` picks which
 * input class it wears so it inherits the surrounding theme's styling.
 *
 * Forwards its ref and spreads `...rest`, so it drops straight into a
 * React Hook Form `register('password')` call the same way a bare `<input>`
 * does. The toggle button is `tabIndex={-1}` — keyboard users tab field to
 * field, the eye is a pointer affordance.
 */
type PasswordInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type'
> & {
  /** Theme class family — `owner` → `.owner-input`, `admin` → `.admin-input`. */
  classPrefix: 'owner' | 'admin'
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ classPrefix, className, ...rest }, ref) {
    const [visible, setVisible] = useState(false)
    const inputClass = `${classPrefix}-input ${className ?? ''}`.trim()

    return (
      <div className="np-password-field">
        <input
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={`${inputClass} np-password-input`}
          {...rest}
        />
        <button
          type="button"
          className="np-password-toggle"
          tabIndex={-1}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    )
  },
)

const svgBase = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

function EyeIcon() {
  return (
    <svg {...svgBase}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function EyeOffIcon() {
  return (
    <svg {...svgBase}>
      <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.4M6.1 6.1A13.3 13.3 0 0 0 2 11s3.5 7 10 7a9 9 0 0 0 4.9-1.44" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="M2 2l20 20" />
    </svg>
  )
}
